import { ContextDocumentDetail } from "@/components/project/ContextDocumentDetail"
import { useProjectSettings } from "@/hooks/useProjectSettings"
import { useNetwork } from "@/hooks/useNetwork"
import { useDocumentRead, useProjectDocuments } from "@/hooks/useReferenceData"
import { Switch } from "@fieldbook/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@fieldbook/ui/tabs"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, MapPin, RefreshCw } from "lucide-react"
import { DocumentFindings } from "./-components/DocumentFindings"
import { DocumentRecords } from "./-components/DocumentRecords"

export const Route = createFileRoute(
  "/_private/projects/$id/data/context/document/$documentId",
)({
  component: ContextDocumentRoute,
})

/**
 * Detail view for an organisation document linked into the project: open/cache
 * the PDF, toggle offline record sync + map visibility, then two tabs — the
 * extracted narrative (Findings) and the species extracted from the document
 * (Records).
 */
function ContextDocumentRoute() {
  const { id: projectId, documentId } = Route.useParams()
  const navigate = Route.useNavigate()

  const { data: documents } = useProjectDocuments(projectId)
  const { settings, updateSettings } = useProjectSettings(projectId)
  const { isOnline } = useNetwork()

  const document = documents.find((d) => d.id === documentId)

  const syncedIds = settings.syncedDocumentIds ?? []
  const mapIds = settings.mapDocumentIds ?? []
  const isSynced = syncedIds.includes(documentId)
  const isOnMap = mapIds.includes(documentId)
  const documentRead = useDocumentRead(
    projectId,
    isOnMap || isSynced ? [documentId] : [],
    syncedIds,
    isOnline,
  )
  const documentOccurrences = documentRead.records
  const isMapDataUnavailableOffline = documentRead.unavailableOffline.length > 0

  const toggleSync = (next: boolean) => {
    updateSettings({
      syncedDocumentIds: next
        ? [...new Set([...syncedIds, documentId])]
        : syncedIds.filter((id) => id !== documentId),
    })
  }

  const toggleOnMap = (next: boolean) => {
    updateSettings({
      mapDocumentIds: next
        ? [...new Set([...mapIds, documentId])]
        : mapIds.filter((id) => id !== documentId),
    })
  }

  const title = document
    ? (document.title ?? document.source_filename ?? "Document")
    : "Document"

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="mb-2 mt-2 space-y-2">
        <button
          onClick={() =>
            navigate({
              to: "/projects/$id/data/context",
              params: { id: projectId },
            })
          }
          className="text-muted-foreground flex items-center gap-1 text-sm focus-visible:outline-0"
        >
          <ArrowLeft size={12} />
          <span>Context</span>
        </button>
        <span className="text-lg font-medium">{title}</span>
        {document && (
          <p className="text-muted-foreground text-xs">
            {document.occurrence_count} records • {document.survey_site_count}{" "}
            survey sites
          </p>
        )}
      </div>

      {/* PDF open + offline copy */}
      {document && (
        <div className="mb-4">
          <ContextDocumentDetail document={document} />
        </div>
      )}

      {/* Config */}
      <div className="border-muted mb-4 flex flex-col gap-3 border-y py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Sync offline</span>
              <span className="text-muted-foreground text-xs">
                Download this document's records for offline use
              </span>
            </div>
          </div>
          <Switch checked={isSynced} onCheckedChange={toggleSync} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Show on map</span>
              <span className="text-muted-foreground text-xs">
                Display these records on the project map
              </span>
            </div>
          </div>
          <Switch checked={isOnMap} onCheckedChange={toggleOnMap} />
        </div>
      </div>

      <Tabs defaultValue="findings">
        <TabsList className="bg-secondary-background grid h-11 w-full grid-cols-2">
          <TabsTrigger value="findings">Findings</TabsTrigger>
          <TabsTrigger value="records">Records</TabsTrigger>
        </TabsList>

        <TabsContent value="findings">
          {document ? (
            <DocumentFindings document={document} />
          ) : (
            <p className="text-muted-foreground text-pretty rounded-lg border border-dashed p-4 text-sm">
              This document is not available on this device.
            </p>
          )}
        </TabsContent>

        <TabsContent value="records">
          <DocumentRecords
            projectId={projectId}
            occurrences={documentOccurrences}
            isRecordDataUnavailableOffline={
              !isSynced && isMapDataUnavailableOffline
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
